import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { withStyles } from "@material-ui/core/styles";
import { Box, CircularProgress, Paper, Tab, Tabs, Tooltip, Typography, WithStyles } from "@material-ui/core";
import { styles } from "./MyReports.styles";
import * as SingleIsinReportAction from "../../../../stores/reports/singleIsin/SingleIsinReportAction";
import { useTranslation } from "react-i18next";
import { selectRequesting } from "../../../../selectors/requesting/RequestingSelector";
import { Breadcrumbs } from "../../../components/Breadcrumbs/Breadcrumbs";
import { CenteredContainer } from "../../../components/CenteredContainer/CenteredContainer";
import { MyReportsSummaryView } from "./MyReportsSummaryView";
import { MyReportsView } from "./MyReportsView";
import { BlueButton } from "../../../components/Buttons/BlueButton";
import { useAuthContext } from "../../../../auth/AuthProvider";
import { CreateReportDialog, IReportData } from "./CreateReportDialog";
import { useSnackbar } from "notistack";
import ToastStatusEnum from "../../../../constants/ToastStatusEnum";
import { convertDateStringToNumber, today } from "../../../../utilities/dateTimeUtils";
import { DATE_FORMAT } from "../../../../constants/TIME";
import { useIsSingleIsinReportAvailable } from "../../../../hooks/useIsSingleIsinReportAvailable";

interface TabPanelProps {
    children?: React.ReactNode;
    index: any;
    value: any;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`download-centre-tabpanel-${index}`}
            aria-labelledby={`download-centre-tab-${index}`}
            {...other}
        >
            {value === index && <Box p={0}>{children}</Box>}
        </div>
    );
}

export interface IMyReportsTabsViewProps extends WithStyles<typeof styles> {}

const MyReportsTabsViewPure: React.FunctionComponent<IMyReportsTabsViewProps> = ({
    classes
}: IMyReportsTabsViewProps) => {
    const authContext = useAuthContext();
    const userId = authContext?.user?.id;

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const defaultDate = convertDateStringToNumber(today().subtract(1, "days").format(DATE_FORMAT));

    const [selectedTab, setSelectedTab] = React.useState(0);
    const [isCreateDialogShown, setIsCreateDialogShown] = useState<boolean>(false);
    const [isinsToCopy, setIsinsToCopy] = useState<string[]>([]);
    const [dateToCopy, setDateToCopy] = useState<number>(defaultDate);

    const hasUserCurrentSubscription = useIsSingleIsinReportAvailable(userId);

    useEffect(() => {
        if (userId && hasUserCurrentSubscription) {
            dispatch(SingleIsinReportAction.requestUserReports(userId));
            dispatch(SingleIsinReportAction.requestUserReportsMonthlySummary(userId));
        }
    }, [dispatch, userId, hasUserCurrentSubscription]);

    const isRequesting = useSelector(state =>
        selectRequesting(state, [
            SingleIsinReportAction.REQUEST_USER_REPORTS,
            SingleIsinReportAction.REQUEST_USER_REPORTS_MONTHLY_SUMMARY
        ])
    );

    const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setSelectedTab(newValue);
    };

    const handleCreateReportClick = () => {
        setIsinsToCopy([]);
        setDateToCopy(defaultDate);
        setIsCreateDialogShown(true);
    };

    const handleCopyReport = (date: number, isins: string[]) => {
        setIsinsToCopy(isins);
        setDateToCopy(date);
        setIsCreateDialogShown(true);
    };

    const handleCreateNewReport = (newReport: IReportData) => {
        setIsCreateDialogShown(false);
        const reportToSend = {
            ...newReport,
            isins: newReport.isins?.map(i => ({ isin: i }))
        };
        dispatch(SingleIsinReportAction.createUserReport(userId, reportToSend))
            .then(() => {
                dispatch(SingleIsinReportAction.requestUserReports(userId));
                dispatch(SingleIsinReportAction.requestUserReportsMonthlySummary(userId));
                enqueueSnackbar("Report has been created", {
                    variant: ToastStatusEnum.Info
                });
            })
            .catch(error =>
                enqueueSnackbar("Report creation failed: " + error?.message, {
                    variant: ToastStatusEnum.Error
                })
            );
    };

    const tabs = [
        {
            title: "My reports",
            view: <MyReportsView onCopyReport={handleCopyReport} isCopyDisabled={!hasUserCurrentSubscription} />
        },
        {
            title: "Summary",
            view: <MyReportsSummaryView />
        }
    ];

    return (
        <CenteredContainer>
            <Breadcrumbs
                items={[
                    { title: t("manageAccount.myAccount"), href: "/my-account" },
                    { title: "My reports", href: "/my-account/my-reports" }
                ]}
            />
            <div className={classes.title}>
                <Typography variant="h3">My single ISIN reports</Typography>
            </div>
            <Tooltip title={!hasUserCurrentSubscription ? "Your subscription is not active" : ""}>
                <span>
                    <BlueButton
                        style={{ margin: 0, marginTop: 20 }}
                        onClick={handleCreateReportClick}
                        disabled={!hasUserCurrentSubscription}
                    >
                        Request new ISIN Report
                    </BlueButton>
                </span>
            </Tooltip>

            <div className={classes.gap}></div>
            {isRequesting ? (
                <div className={classes.center}>
                    <CircularProgress color="inherit" className={classes.center} />
                </div>
            ) : (
                <Paper square>
                    <Tabs value={selectedTab} indicatorColor="primary" textColor="primary" onChange={handleTabChange}>
                        {tabs.map((tabs, index) => (
                            <Tab key={tabs.title} label={tabs.title} />
                        ))}
                    </Tabs>
                    {tabs.map((tab, index) => {
                        return (
                            <TabPanel value={selectedTab} key={index} index={index}>
                                {tab.view}
                            </TabPanel>
                        );
                    })}
                </Paper>
            )}
            {isCreateDialogShown && (
                <CreateReportDialog
                    isins={isinsToCopy}
                    dialogType="createReportByUser"
                    date={dateToCopy}
                    onConfirm={handleCreateNewReport}
                    onClose={() => setIsCreateDialogShown(false)}
                />
            )}
        </CenteredContainer>
    );
};

export const MyReportsTabsView = withStyles(styles)(MyReportsTabsViewPure);
